import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Users,
  Clock,
  DollarSign,
  BarChart3,
  PieChart,
  RefreshCw,
  ArrowLeft,
  FileSpreadsheet,
  Table,
  Eye,
  Settings,
  Filter,
  Grid,
  List
} from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { ReportExporter, type ExportFormat } from './utils/reportExport';

interface ReportData {
  reportId: string;
  reportType: string;
  title: string;
  generatedAt: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  summary: Record<string, any>;
  data: any[];
  charts?: Array<{
    type: 'bar' | 'line' | 'pie' | 'doughnut';
    title: string;
    data: any[];
    labels?: string[];
  }>;
  metadata?: {
    recordCount: number;
    processingTime: number;
    filters?: Record<string, any>;
  };
}

interface ReportViewerProps {
  reportType: string;
  onBack: () => void;
}

type ViewMode = 'summary' | 'table' | 'charts' | 'raw';
type ExportFormat = 'pdf' | 'excel' | 'csv' | 'json' | 'png';

export const ReportViewer: React.FC<ReportViewerProps> = ({ reportType, onBack }) => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [selectedChart, setSelectedChart] = useState<number>(0);

  // Force show filters for attendance reports
  const isAttendanceReport = reportType === 'attendance';
  const [showFilters, setShowFilters] = useState(isAttendanceReport);

  // Debug logging
  console.log('🔍 ReportViewer initialized:', { reportType, isAttendanceReport, showFilters });

  // Date range state - for attendance reports, default to current month
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0]
    };
  };

  const defaultRange = isAttendanceReport ? getCurrentMonthRange() : { startDate: '', endDate: '' };
  const [startDate, setStartDate] = useState<string>(defaultRange.startDate);
  const [endDate, setEndDate] = useState<string>(defaultRange.endDate);
  const [useAllData, setUseAllData] = useState(!isAttendanceReport);

  // Effect to handle reportType changes
  useEffect(() => {
    console.log('🔍 ReportViewer loading for reportType:', reportType, 'isAttendanceReport:', isAttendanceReport);

    // Force show filters for attendance reports
    if (isAttendanceReport) {
      console.log('🔧 Showing filters for attendance report');
      setShowFilters(true);
    } else {
      console.log('🔧 Hiding filters for non-attendance report');
      setShowFilters(false);
    }

    fetchReportData();
  }, [reportType, isAttendanceReport]);

  // Additional effect to ensure filters are visible for attendance reports
  useEffect(() => {
    if (isAttendanceReport && !showFilters) {
      console.log('🔧 Force enabling filters for attendance report');
      setShowFilters(true);
    }
  }, [isAttendanceReport, showFilters]);

  // Fetch data when date range changes
  useEffect(() => {
    if (!useAllData && startDate && endDate) {
      fetchReportData();
    }
  }, [startDate, endDate, useAllData]);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      // Build API parameters
      const params: any = {};

      if (!useAllData && startDate && endDate) {
        // Use selected date range
        params.startDate = startDate;
        params.endDate = endDate;
      }
      // If useAllData is true or no dates selected, don't send date params to get all data

      console.log('📊 Fetching report data:', { reportType, params, useAllData });

      const response = await api.get(`/firm-admin/reports/view/${reportType}`, {
        params
      });

      console.log('📊 Report API Response:', response.data);

      if (response.data.success) {
        const rawData = response.data.data;
        console.log('📊 Raw Report Data:', rawData);

        // Transform the data to match the expected ReportData interface
        let tableData = [];

        // Extract table data based on report type
        if (reportType === 'financial' && rawData.invoiceBreakdown?.byClient) {
          tableData = rawData.invoiceBreakdown.byClient;
        } else if (reportType === 'task-performance' && rawData.performanceByAssignee) {
          // Remove assigneeId and format totalHours for task performance
          tableData = rawData.performanceByAssignee.map((item: any) => {
            const { assigneeId, ...rest } = item;
            return {
              ...rest,
              totalHours: typeof item.totalHours === 'number' ? Number(item.totalHours.toFixed(2)) : item.totalHours
            };
          });
        } else if (reportType === 'client-activity' && rawData.clientDetails) {
          // Format client activity data and remove clientId
          tableData = rawData.clientDetails.map((item: any) => {
            const { clientId, ...rest } = item;
            return {
              ...rest,
              totalRevenue: typeof item.totalRevenue === 'number' ? Number(item.totalRevenue.toFixed(2)) : item.totalRevenue,
              lastActivity: item.lastActivity && item.lastActivity !== -Infinity
                ? new Date(item.lastActivity).toLocaleDateString()
                : 'No Activity'
            };
          });
        } else if (reportType === 'time-tracking' && rawData.timeBreakdown?.byStaff) {
          tableData = rawData.timeBreakdown.byStaff;
        } else if (reportType === 'attendance' && rawData.byStaff) {
          tableData = rawData.byStaff;
        } else {
          // Fallback to generic data fields
          tableData = rawData.data || rawData.details || [];
        }

        // Format summary data for task performance reports
        let formattedSummary = rawData.summary || rawData.executiveSummary || {};
        if (reportType === 'task-performance' && formattedSummary.totalHoursSpent) {
          formattedSummary = {
            ...formattedSummary,
            totalHoursSpent: typeof formattedSummary.totalHoursSpent === 'number'
              ? Number(formattedSummary.totalHoursSpent.toFixed(2))
              : formattedSummary.totalHoursSpent
          };
        }

        const transformedData: ReportData = {
          reportId: rawData.reportId || `${reportType}-${Date.now()}`,
          reportType: reportType,
          title: rawData.title || `${reportType.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} Report`,
          generatedAt: rawData.generatedAt || new Date().toISOString(),
          dateRange: rawData.dateRange || rawData.period || {
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
          },
          summary: formattedSummary,
          data: tableData,
          charts: rawData.charts || [],
          metadata: rawData.metadata || {
            recordCount: tableData?.length || 0,
            processingTime: 0
          }
        };

        console.log('📊 Transformed Report Data:', transformedData);
        setReportData(transformedData);
      } else {
        throw new Error('Failed to fetch report data');
      }
    } catch (error: any) {
      console.error('❌ Error fetching report data:', error);
      console.error('❌ Error response:', error.response?.data);

      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.response?.data?.error ||
                          error.message ||
                          'Failed to load report data';

      toast.error(errorMessage);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: ExportFormat) => {
    try {
      setExporting(true);

      // Use the same date range as the current report
      let exportDateRange;

      if (reportData?.dateRange?.startDate === null && reportData?.dateRange?.endDate === null) {
        // Report Viewer is showing all data - export all data too (no date range)
        exportDateRange = undefined;
      } else {
        // Report Viewer has specific date range - use it for export
        exportDateRange = reportData?.dateRange || {
          startDate: startDate || undefined,
          endDate: endDate || undefined
        };
      }

      await ReportExporter.exportReport({
        reportType,
        format,
        dateRange: exportDateRange,
        filename: `${reportData?.title || reportType}-report-${new Date().toISOString().split('T')[0]}`
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      // Error handling is done in ReportExporter
    } finally {
      setExporting(false);
    }
  };

  const getReportIcon = (type: string) => {
    const icons = {
      revenue: DollarSign,
      clients: Users,
      tasks: FileText,
      timetracking: Clock,
      attendance: Calendar,
      financial: BarChart3
    };
    return icons[type as keyof typeof icons] || FileText;
  };

  const formatValue = (key: string, value: any) => {
    if (typeof value === 'boolean') {
      if (key.toLowerCase().includes('active')) {
        return value ? 'Yes' : 'No';
      }
      return value ? 'True' : 'False';
    }
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('rate') || key.toLowerCase().includes('percentage')) {
        return `${value}%`;
      }
      if (key.toLowerCase().includes('revenue') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('profit')) {
        return `$${value.toLocaleString()}`;
      }
      if (key.toLowerCase().includes('hours') || key.toLowerCase().includes('time')) {
        return `${value.toFixed(2)}h`;
      }
      return value.toLocaleString();
    }
    return value;
  };

  const renderDataTable = () => {
    if (!reportData?.data || reportData.data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No data available for this report
        </div>
      );
    }

    // Filter out User ID columns
    const allColumns = Object.keys(reportData.data?.[0] || {});
    const columns = allColumns.filter(column =>
      !column.toLowerCase().includes('userid') &&
      !column.toLowerCase().includes('user_id') &&
      column.toLowerCase() !== 'id' // Also remove generic 'id' column
    );

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reportData.data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatValue(column, row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChart = (chart: any, index: number) => {
    // Simple chart visualization using CSS
    if (chart.type === 'bar' && chart.data) {
      const maxValue = Math.max(...chart.data.map((item: any) => item.value || 0));

      return (
        <div key={index} className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">{chart.title}</h4>
          <div className="space-y-2">
            {chart.data.map((item: any, itemIndex: number) => (
              <div key={itemIndex} className="flex items-center space-x-3">
                <div className="w-24 text-sm font-medium text-gray-700 truncate">
                  {item.label || item.name}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                  <div
                    className="bg-primary-500 h-6 rounded-full flex items-center justify-end pr-2"
                    style={{ width: `${((item.value || 0) / maxValue) * 100}%` }}
                  >
                    <span className="text-xs text-white font-medium">
                      {formatValue('value', item.value)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (chart.type === 'pie' && chart.data) {
      const total = chart.data.reduce((sum: number, item: any) => sum + (item.value || 0), 0);

      return (
        <div key={index} className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">{chart.title}</h4>
          <div className="grid grid-cols-2 gap-4">
            {chart.data.map((item: any, itemIndex: number) => {
              const percentage = total > 0 ? ((item.value || 0) / total) * 100 : 0;
              const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500'];

              return (
                <div key={itemIndex} className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${colors[itemIndex % colors.length]}`} />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{item.label || item.name}</div>
                    <div className="text-xs text-gray-500">
                      {formatValue('value', item.value)} ({percentage.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="text-center py-8 text-gray-500">
        Chart visualization for {chart.type} not implemented yet
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Reports
            </button>
          </div>

          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-gray-600">Loading {reportType.replace('-', ' ')} report...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Reports
            </button>
          </div>

          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Failed to load report data</p>
            <button
              onClick={fetchReportData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ReportIcon = getReportIcon(reportType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <ReportIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {reportData.title}
                {isAttendanceReport && (
                  <span className="ml-3 text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                    📅 Date Filtering Available
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-500">
                Generated on {new Date(reportData.generatedAt).toLocaleDateString()}
                {reportData.dateRange && reportData.dateRange.startDate && reportData.dateRange.endDate ? (
                  <> • {new Date(reportData.dateRange.startDate).toLocaleDateString()} - {new Date(reportData.dateRange.endDate).toLocaleDateString()}</>
                ) : (
                  <> • All Data (Entire History)</>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={fetchReportData}
            disabled={loading}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>



          {/* Export Dropdown */}
          <div className="relative">
            <select
              onChange={(e) => e.target.value && exportReport(e.target.value as ExportFormat)}
              disabled={exporting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 appearance-none pr-8"
              defaultValue=""
            >
              <option value="" disabled>
                {exporting ? 'Exporting...' : 'Export As'}
              </option>
              <option value="pdf">PDF</option>
              <option value="excel">Excel (XLSX)</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              {reportData?.charts && reportData.charts.length > 0 && (
                <option value="png">Chart (PNG)</option>
              )}
            </select>
            <Download className="w-4 h-4 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-white" />
          </div>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'summary', label: 'Summary', icon: Eye },
            { id: 'table', label: 'Data Table', icon: Table },
            { id: 'charts', label: 'Charts', icon: BarChart3, disabled: !reportData?.charts?.length },
            { id: 'raw', label: 'Raw Data', icon: FileText }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setViewMode(tab.id as ViewMode)}
                disabled={tab.disabled}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  viewMode === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : tab.disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className={`mr-2 h-5 w-5 ${
                  viewMode === tab.id ? 'text-primary-500' :
                  tab.disabled ? 'text-gray-300' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Date Filters Section - Show for attendance reports when filters are enabled */}
      {isAttendanceReport && showFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                📅 Date Range Filter
                {isAttendanceReport && (
                  <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    Recommended for Attendance Reports
                  </span>
                )}
              </h3>
              {isAttendanceReport && (
                <button
                  onClick={() => {
                    const range = getCurrentMonthRange();
                    setStartDate(range.startDate);
                    setEndDate(range.endDate);
                    setUseAllData(false);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Set Current Month
                </button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="dateFilter"
                  checked={useAllData}
                  onChange={() => setUseAllData(true)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">All Data (Entire History)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="dateFilter"
                  checked={!useAllData}
                  onChange={() => setUseAllData(false)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Custom Date Range</span>
              </label>
            </div>

            {!useAllData && (
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <button
                  onClick={fetchReportData}
                  disabled={loading || !startDate || !endDate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  Apply Filter
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'summary' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(reportData.summary || {}).map(([key, value]) => (
              <div key={key} className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatValue(key, value)}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Charts Preview */}
          {reportData.charts && reportData.charts.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Quick Insights</h3>
                <button
                  onClick={() => setViewMode('charts')}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View All Charts →
                </button>
              </div>
              {renderChart(reportData.charts[0], 0)}
            </div>
          )}

          {/* Data Preview */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Data Preview</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    First 5 rows of {reportData.metadata?.recordCount || reportData.data?.length || 0} total records
                  </p>
                </div>
                <button
                  onClick={() => setViewMode('table')}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View Full Table →
                </button>
              </div>
            </div>
            <div className="p-6">
              {reportData.data && reportData.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(reportData.data?.[0] || {})
                          .filter(column =>
                            !column.toLowerCase().includes('userid') &&
                            !column.toLowerCase().includes('user_id') &&
                            column.toLowerCase() !== 'id'
                          )
                          .map((column) => (
                          <th
                            key={column}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.data.slice(0, 5).map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          {Object.keys(reportData.data?.[0] || {})
                            .filter(column =>
                              !column.toLowerCase().includes('userid') &&
                              !column.toLowerCase().includes('user_id') &&
                              column.toLowerCase() !== 'id'
                            )
                            .map((column) => (
                            <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatValue(column, row[column])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No data available for preview
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {viewMode === 'table' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Complete Data Table</h2>
                <p className="text-sm text-gray-500 mt-1">
                  All {reportData.metadata?.recordCount || reportData.data?.length || 0} records
                </p>
              </div>

            </div>
          </div>



          <div className="p-6">
            {renderDataTable()}
          </div>
        </div>
      )}

      {viewMode === 'charts' && reportData.charts && (
        <div className="space-y-6">
          {reportData.charts.length > 1 && (
            <div className="flex items-center space-x-2 overflow-x-auto pb-2">
              {reportData.charts.map((chart, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedChart(index)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedChart === index
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {chart.title}
                </button>
              ))}
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            {reportData.charts[selectedChart] && renderChart(reportData.charts[selectedChart], selectedChart)}
          </div>
        </div>
      )}

      {viewMode === 'raw' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Raw JSON Data</h2>
            <p className="text-sm text-gray-500 mt-1">
              Complete report data in JSON format
            </p>
          </div>
          <div className="p-6">
            <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm">
              {JSON.stringify(reportData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
